import { Button } from '@pantry/ui'
import React, { useCallback, useState } from 'react'
import { EMAIL_RE } from '../campaignValidation'
import {
  errorTextStyle,
  helpTextStyle,
  inputStyle,
  labelStyle,
} from '../styles'

// Body of the review step's "Test Before Sending" card (the page wraps it in
// the shared <BuilderCard>, so the card chrome stays identical to the Campaign
// Summary / Pre-launch Checklist cards next to it).
//
// EMAIL ONLY, deliberately: the test-send endpoint accepts an email address
// and nothing else, so the page renders this card only for email campaigns.
// It used to take an `isSms` prop whose branches were unreachable — including
// the one that skipped address validation — which meant the day SMS is
// switched on the card would have started accepting any non-empty string as a
// phone number. The prop is gone; enabling SMS test sends has to come with a
// real phone validator (and a backend that accepts one) rather than silently
// inheriting an unvalidated path.
//
// PERFORMANCE: the address lives in THIS component's local state on purpose.
// CampaignBuilderPage is ~2.6k lines and was just optimised for typing lag, so
// the test address must never become page-level state — a keystroke here has
// to re-render this card and nothing else. That only holds while every prop
// below stays referentially stable across page renders (`onSend` is a
// useCallback that reads the content snapshot from a ref), which is what makes
// the React.memo wrapper effective.
interface CampaignTestSendCardProps {
  sending: boolean
  onSend: (address: string) => void
}

const CampaignTestSendCardBase: React.FC<CampaignTestSendCardProps> = ({
  sending,
  onSend,
}) => {
  const [address, setAddress] = useState('')
  const [error, setError] = useState('')

  const submit = useCallback(() => {
    // The Enter key path goes through here too, so the in-flight guard has to
    // live in the handler — not only on the button's `disabled`. The mutation
    // has no mutationKey, so react-query would happily run a held-down Enter
    // key's worth of concurrent test sends against an endpoint that is not
    // rate-limited server-side.
    if (sending) return
    const value = address.trim()
    if (!value) return
    if (!EMAIL_RE.test(value)) {
      setError('Enter a valid email address')
      return
    }
    setError('')
    onSend(value)
  }, [address, sending, onSend])

  return (
    <div>
      <label style={labelStyle} htmlFor="campaign-test-send-address">
        Send test email to
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <input
          id="campaign-test-send-address"
          type="email"
          value={address}
          placeholder="you@example.com"
          onChange={(e) => {
            setAddress(e.target.value)
            if (error) setError('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          style={{ ...inputStyle, flex: 1 }}
        />
        <Button
          variant="ghost"
          onClick={submit}
          disabled={sending || address.trim().length === 0}
        >
          {sending ? 'Sending…' : 'Send Test'}
        </Button>
      </div>
      {error && <div style={{ ...errorTextStyle, marginTop: 4 }}>{error}</div>}
      <div style={helpTextStyle}>
        Sends a preview to yourself before going live. Personalisation tokens
        will use placeholder values.
      </div>
    </div>
  )
}

export const CampaignTestSendCard = React.memo(CampaignTestSendCardBase)
CampaignTestSendCard.displayName = 'CampaignTestSendCard'
